import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import {
  CheckCircle, XCircle, WarningCircle, ArrowLeft, Sparkle, Ruler, Truck, ArrowRight
} from "@phosphor-icons/react";
import DesignActions from "@/components/DesignActions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DesignReview() {
  const { orderNumber } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [reason, setReason] = useState("");
  const [done, setDone] = useState(null); // "confirmed" | "rejected"

  const previewUrl = `${API}/public/track/${orderNumber}/design-preview`;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/track/${orderNumber}`);
        if (alive) setOrder(data);
      } catch (e) {
        if (alive) setErr(e.response?.data?.detail || "Order not found");
      }
    })();
    return () => { alive = false; };
  }, [orderNumber]);

  const confirmDesign = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/public/track/${orderNumber}/confirm-design`);
      setDone("confirmed");
    } catch (e) {
      setErr(e.response?.data?.detail || "Something went wrong");
    } finally { setBusy(false); }
  };

  const rejectDesign = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/public/track/${orderNumber}/reject-design`, { reason });
      setDone("rejected");
    } catch (e) {
      setErr(e.response?.data?.detail || "Something went wrong");
    } finally { setBusy(false); }
  };

  const approvalStatus = order?.design_approval_status;
  const alreadyConfirmed = approvalStatus === "confirmed";
  const alreadyRejected = approvalStatus === "rejected";

  return (
    <div className="min-h-screen bp-grid relative overflow-hidden" data-testid="design-review-page">
      <header className="border-b border-slate-800/60 bg-slate-950/60 backdrop-blur relative z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => nav(`/track/${orderNumber}`)}
            className="text-slate-400 hover:text-white p-1"
            aria-label="Back"
          >
            <ArrowLeft size={20} />
          </button>
          <img
            src="/logo.png"
            alt="Shobhit Creation"
            className="h-9 w-auto object-contain"
            data-testid="brand-logo"
          />
          <div className="flex-1">
            <div className="text-[10px] font-mono-num text-orange-400 tracking-widest">
              // DESIGN REVIEW · #{orderNumber}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-5 relative z-10">
        {err && !order && (
          <div className="surface p-10 rounded-sm text-center" data-testid="design-review-error">
            <div className="w-14 h-14 stripe-accent mx-auto mb-4" />
            <div className="font-heading text-2xl font-black text-white">{err}</div>
          </div>
        )}

        {order && !done && (
          <>
            {/* Header greeting */}
            <div className="surface p-5 md:p-6 rounded-sm relative overflow-hidden">
              <Sparkle className="absolute top-3 right-4 text-slate-700/50 pointer-events-none" size={36} weight="fill"/>
              <div className="text-xs font-mono-num tracking-widest text-orange-400">// NAMASTE</div>
              <h1 className="font-heading text-3xl md:text-4xl font-black text-white mt-1 leading-tight">
                {order.customer_name || "Customer"} ji 🙏
              </h1>
              <p className="text-slate-300 text-sm md:text-base mt-3">
                Aapke order <b className="text-orange-400 font-mono-num">#{order.order_number}</b> ka design ready hai.
                Neeche image dhyaan se dekh lein — <b className="text-red-400">size aur design dono check karein.</b>
              </p>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs font-mono-num">
                <InfoTile label="Total Amount" value={`₹${(order.total_amount||0).toLocaleString("en-IN")}`}/>
                <InfoTile label="Sqft" value={`${order.total_sqft || 0}`} icon={<Ruler size={11} weight="bold"/>}/>
                <InfoTile label="Balance" value={`₹${(order.balance||0).toLocaleString("en-IN")}`}/>
              </div>
            </div>

            {/* Critical warning */}
            <div className="rounded-sm border-2 border-red-500/60 bg-red-500/10 p-4 md:p-5 flex items-start gap-3">
              <WarningCircle size={28} weight="fill" className="text-red-400 shrink-0 mt-0.5"/>
              <div>
                <div className="font-heading font-black text-red-300 tracking-wider text-sm md:text-base">
                  CHECK DESIGN AND SIZE VERY CAREFULLY
                </div>
                <div className="text-slate-200 text-sm mt-1 leading-relaxed">
                  Cutting shuru ho jaane ke baad <b className="text-red-300">kisi bhi change ki request nahi le sakte</b> —
                  material waste hoga aur extra charges lagenge. Confirm karne se pehle image ke saare dimensions aur design carefully check kariye.
                </div>
              </div>
            </div>

            {/* Design preview */}
            <div className="surface rounded-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between gap-2 flex-wrap">
                <div className="text-xs font-mono-num tracking-widest text-slate-400">
                  // YOUR DESIGN
                </div>
                <DesignActions
                  fileUrl={previewUrl}
                  fileName={`design-${order.order_number}.png`}
                  orderNumber={order.order_number}
                  trackUrl={`${window.location.origin}/track/${order.order_number}`}
                  compact
                />
              </div>
              <div className="bg-slate-950 p-3 flex items-center justify-center min-h-[240px] relative">
                <img
                  src={previewUrl}
                  alt={`Design for order ${order.order_number}`}
                  className="max-w-full max-h-[560px] object-contain"
                  onError={(e) => {
                    const parent = e.currentTarget.parentElement;
                    e.currentTarget.style.display = "none";
                    if (parent && !parent.querySelector("[data-design-fallback]")) {
                      const div = document.createElement("div");
                      div.setAttribute("data-design-fallback", "1");
                      div.className = "text-center px-4 py-8";
                      div.innerHTML =
                        '<div style="color:#f87171;font-weight:800;letter-spacing:.1em;font-size:12px">DESIGN LOAD NAHI HO PAYA</div>' +
                        '<div style="color:#94a3b8;font-size:13px;margin-top:8px">Neeche button pe click karke design directly download karein.</div>' +
                        `<a href="${previewUrl}" target="_blank" rel="noreferrer" style="display:inline-block;margin-top:14px;padding:10px 18px;background:#f97316;color:#fff;border-radius:4px;text-decoration:none;font-weight:800;letter-spacing:.15em;font-size:12px">OPEN DESIGN FILE</a>`;
                      parent.appendChild(div);
                    }
                  }}
                  data-testid="design-preview-image"
                />
              </div>
            </div>

            {alreadyConfirmed && (
              <div className="rounded-sm border-2 border-emerald-500/60 bg-emerald-500/10 p-4 text-center">
                <CheckCircle size={32} weight="fill" className="text-emerald-400 mx-auto"/>
                <div className="font-heading text-emerald-300 mt-2">Aap already confirm kar chuke hain ✓</div>
                <div className="text-slate-400 text-xs mt-1">Order production queue me hai.</div>
                {/* Prominent "TRACK YOUR ORDER" CTA — appears the moment the customer
                    confirms so they can immediately see production progress. */}
                <Link
                  to={`/track/${orderNumber}`}
                  className="mt-4 inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 rounded-sm bg-orange-500 hover:bg-orange-600 transition-colors text-white font-heading font-black tracking-widest text-sm sm:text-base shadow-lg shadow-orange-500/25"
                  data-testid="track-order-cta"
                >
                  <Truck size={18} weight="fill" />
                  TRACK YOUR ORDER
                  <ArrowRight size={16} weight="bold" />
                </Link>
                <div className="text-[10px] font-mono-num text-slate-500 mt-2 tracking-widest">
                  YAHAN CLICK KAREIN — LIVE STATUS DEKH SAKENGE
                </div>
              </div>
            )}
            {alreadyRejected && (
              <div className="rounded-sm border-2 border-amber-500/60 bg-amber-500/10 p-4 text-center">
                <XCircle size={32} weight="fill" className="text-amber-400 mx-auto"/>
                <div className="font-heading text-amber-300 mt-2">Aap ne design reject kiya tha</div>
                {order.design_rejection_reason && (
                  <div className="text-slate-300 text-xs mt-1">Reason: <b>{order.design_rejection_reason}</b></div>
                )}
                <div className="text-slate-400 text-xs mt-1">Team ne naya design upload karne ke liye note kar liya hai.</div>
              </div>
            )}

            {/* Action buttons (only if pending approval) */}
            {!alreadyConfirmed && !alreadyRejected && !showReject && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button
                  onClick={confirmDesign}
                  disabled={busy}
                  className="h-14 rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white font-heading tracking-widest text-base shadow-[0_8px_24px_rgba(16,185,129,0.35)] hover:-translate-y-0.5 transition-transform"
                  data-testid="confirm-design-btn"
                >
                  <CheckCircle size={20} weight="fill" className="mr-2"/>
                  {busy ? "CONFIRMING…" : "CONFIRM DESIGN"}
                </Button>
                <Button
                  onClick={() => setShowReject(true)}
                  disabled={busy}
                  className="h-14 rounded-sm bg-amber-600 hover:bg-amber-500 text-white font-heading tracking-widest text-base shadow-[0_8px_24px_rgba(217,119,6,0.35)] hover:-translate-y-0.5 transition-transform"
                  data-testid="reject-design-open-btn"
                >
                  <XCircle size={20} weight="fill" className="mr-2"/>
                  NOT CONFIRM
                </Button>
              </div>
            )}

            {showReject && !alreadyConfirmed && !alreadyRejected && (
              <div className="surface p-5 rounded-sm space-y-3">
                <div className="text-xs font-mono-num tracking-widest text-amber-400">// KYA CHANGE CHAHIYE?</div>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  placeholder="Design me kya changes chahiye? (optional, but helpful for our team)"
                  className="w-full px-3 py-2 bg-slate-800/60 border border-slate-600 text-white text-sm rounded-sm resize-none focus:outline-none focus:ring-1 focus:ring-amber-500"
                  data-testid="reject-reason-input"
                />
                <div className="flex gap-2 justify-end">
                  <Button onClick={() => { setShowReject(false); setReason(""); }} variant="outline"
                    className="rounded-sm h-11 border-slate-600 text-slate-300 hover:bg-slate-800">
                    Cancel
                  </Button>
                  <Button onClick={rejectDesign} disabled={busy}
                    className="rounded-sm h-11 bg-amber-600 hover:bg-amber-500 text-white font-heading tracking-widest"
                    data-testid="reject-design-submit">
                    {busy ? "SUBMITTING…" : "SUBMIT REJECTION"}
                  </Button>
                </div>
              </div>
            )}

            {/* Fallback link to full tracking */}
            <div className="text-center pt-4 text-xs font-mono-num text-slate-500">
              <Link to={`/track/${orderNumber}`} className="hover:text-orange-400 transition-colors">
                View full order tracking →
              </Link>
            </div>
          </>
        )}

        {done === "confirmed" && (
          <div className="surface p-10 rounded-sm text-center" data-testid="design-review-confirmed">
            <CheckCircle size={64} weight="fill" className="text-emerald-400 mx-auto"/>
            <h2 className="font-heading text-3xl font-black text-white mt-4">Confirmed ✓</h2>
            <p className="text-slate-300 mt-2">
              Thank you! Aapke order ki cutting shuru ho jaayegi.
            </p>
            <Link
              to={`/track/${orderNumber}`}
              className="inline-block mt-6 px-6 py-3 rounded-full bg-orange-500 hover:bg-orange-400 text-white font-heading tracking-widest text-sm"
            >
              TRACK YOUR ORDER
            </Link>
          </div>
        )}
        {done === "rejected" && (
          <div className="surface p-10 rounded-sm text-center" data-testid="design-review-rejected">
            <XCircle size={64} weight="fill" className="text-amber-400 mx-auto"/>
            <h2 className="font-heading text-3xl font-black text-white mt-4">Rejection Received</h2>
            <p className="text-slate-300 mt-2">
              Team ko notify kar diya gaya hai. Naya design taiyaar hoke phir aapko share kiya jaayega.
            </p>
            <Link
              to={`/track/${orderNumber}`}
              className="inline-block mt-6 px-6 py-3 rounded-full bg-slate-700 hover:bg-slate-600 text-white font-heading tracking-widest text-sm"
            >
              VIEW TRACKING
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}

function InfoTile({ label, value, icon }) {
  return (
    <div className="surface-2 rounded-sm p-2">
      <div className="text-[9px] text-slate-500 tracking-widest">{label.toUpperCase()}</div>
      <div className="text-slate-100 font-black flex items-center gap-1 mt-0.5">
        {icon} {value}
      </div>
    </div>
  );
}
